import React from 'react'
import { Card, CardContent, CardHeader, Button } from '@/shared/ui'
import { Table, Th, Td, Tr } from '@/shared/ui/Table'
import type { AffiliateLead } from '../types'
import { Mail, Instagram, Facebook, Youtube, MessageCircle, Phone, MoreVertical } from 'lucide-react'
import { DropdownMenu } from '@/shared/ui/DropdownMenu'

export interface AffiliateLeadsTableProps {
  items: AffiliateLead[]
  onViewNotes: (lead: AffiliateLead) => void
  onEdit: (lead: AffiliateLead) => void
  onDelete: (lead: AffiliateLead) => void
}

function formatDate(value: string | null): string {
  if (!value) return ''
  try {
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return ''
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return ''
  }
}

function SocialLinks({ lead }: { lead: AffiliateLead }) {
  const items: { key: string; label: string; icon: React.ReactNode; href?: string }[] = []

  if (lead.social_instagram) {
    items.push({
      key: 'instagram',
      label: 'Instagram',
      icon: <Instagram className="h-4 w-4" />,
      href: lead.social_instagram,
    })
  }
  if (lead.social_facebook) {
    items.push({
      key: 'facebook',
      label: 'Facebook',
      icon: <Facebook className="h-4 w-4" />,
      href: lead.social_facebook,
    })
  }
  if (lead.social_youtube) {
    items.push({
      key: 'youtube',
      label: 'YouTube',
      icon: <Youtube className="h-4 w-4" />,
      href: lead.social_youtube,
    })
  }
  if (lead.social_discord) {
    items.push({
      key: 'discord',
      label: 'Discord',
      icon: <MessageCircle className="h-4 w-4" />,
      href: lead.social_discord,
    })
  }
  if (lead.social_other) {
    items.push({
      key: 'other',
      label: 'Outros',
      icon: <MessageCircle className="h-4 w-4" />,
      href: lead.social_other,
    })
  }

  if (items.length === 0) return <span className="text-xs text-foreground/60">Nenhuma mídia informada</span>

  return (
    <div className="flex flex-wrap items-center gap-2">
      {items.map((item) =>
        item.href ? (
          <a
            key={item.key}
            href={item.href}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 rounded-full border border-border/60 px-2 py-0.5 text-xs text-foreground/80 hover:bg-muted"
          >
            {item.icon}
            <span>{item.label}</span>
          </a>
        ) : (
          <span
            key={item.key}
            className="inline-flex items-center gap-1 rounded-full border border-border/60 px-2 py-0.5 text-xs text-foreground/80"
          >
            {item.icon}
            <span>{item.label}</span>
          </span>
        ),
      )}
    </div>
  )
}

export default function AffiliateLeadsTable({ items, onViewNotes, onEdit, onDelete }: AffiliateLeadsTableProps) {
  if (!items.length) {
    return (
      <Card>
        <CardContent className="py-6 text-sm text-foreground/70">
          Ainda não há interessados cadastrados pelo formulário de afiliados.
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 py-3">
        <div>
          <p className="text-sm font-medium">Interessados no programa de afiliados</p>
          <p className="text-xs text-foreground/60">
            Leads capturados via landing pública, ordenados dos mais recentes para os mais antigos.
          </p>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <Table>
          <thead>
            <Tr>
              <Th>Data</Th>
              <Th>Nome</Th>
              <Th>E-mail</Th>
              <Th>WhatsApp</Th>
              <Th>Mídias sociais</Th>
              <Th className="w-14 text-right">Ações</Th>
            </Tr>
          </thead>
          <tbody>
            {items.map((lead) => (
              <Tr key={lead.id}>
                <Td className="whitespace-nowrap text-xs text-foreground/70">
                  {formatDate(lead.created_at)}
                </Td>
                <Td className="text-sm font-medium">{lead.full_name}</Td>
                <Td className="text-sm">
                  <a
                    href={`mailto:${lead.email}`}
                    className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline"
                  >
                    <Mail className="h-3.5 w-3.5" />
                    <span>{lead.email}</span>
                  </a>
                </Td>
                <Td className="whitespace-nowrap text-xs text-foreground/80">
                  {lead.whatsapp ? (
                    <a
                      href={`https://wa.me/${lead.whatsapp.replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline"
                    >
                      <Phone className="h-3.5 w-3.5" />
                      <span>{lead.whatsapp}</span>
                    </a>
                  ) : (
                    <span className="text-foreground/50">—</span>
                  )}
                </Td>
                <Td className="max-w-xs">
                  <SocialLinks lead={lead} />
                </Td>
                <Td className="text-right">
                  <DropdownMenu
                    align="end"
                    trigger={
                      <Button variant="outline" className="h-8 w-8 p-0">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    }
                    items={[
                      {
                        label: 'Observações',
                        onSelect: () => onViewNotes(lead),
                      },
                      {
                        label: 'Editar informações',
                        onSelect: () => onEdit(lead),
                      },
                      {
                        label: <span className="text-destructive">Excluir</span>,
                        onSelect: () => onDelete(lead),
                      },
                    ]}
                  />
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </CardContent>
    </Card>
  )
}
